import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["items", "onItemClick", "onTrackInteraction", "onFullscreenChange"];
import { GREAT_WHITE, HEFFALUMP, KOALA, OBSIDIAN, SLINKY } from 'HubStyleTokens/colors';
import { BUTTON_BORDER_WIDTH, DISTANCE_MEASUREMENT_EXTRA_SMALL, DISTANCE_MEASUREMENT_MEDIUM, MEDIUM_CLOSE_BUTTON_SIZE } from 'HubStyleTokens/sizes';
import { CARD_SHADOW_COLOR } from 'HubStyleTokens/theme';
import UIClickable from 'UIComponents/button/UIClickable';
import UICloseButton from 'UIComponents/button/UICloseButton';
import UICarousel from 'UIComponents/carousel/UICarousel';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIFullScreen from 'UIComponents/dialog/UIFullScreen';
import UIModal from 'UIComponents/dialog/UIModal';
import UIImage from 'UIComponents/image/UIImage';
import UIFlex from 'UIComponents/layout/UIFlex';
import VideoPlayer from 'marketplace-ui-common/components/VideoPlayer';
import { useState } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export default function MediaCarousel(_ref) {
  let {
      items,
      onItemClick,
      onTrackInteraction,
      onFullscreenChange
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [fullscreen, setFullscreen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  return /*#__PURE__*/_jsx(Fullscreen, {
    fullscreen: fullscreen,
    onClose: () => {
      // Wait a frame so that marketplace-embed knows we were previously
      // fullscreen. Otherwise, the entire modal/panel will close.
      requestAnimationFrame(() => {
        setFullscreen(false);
        onFullscreenChange === null || onFullscreenChange === void 0 || onFullscreenChange(false);
      });
    },
    children: /*#__PURE__*/_jsx(UICarousel
    // styling
    , Object.assign({
      className: "p-x-0",
      maxWidth: "100%"
      // steps
      ,
      stepIndex: stepIndex,
      onStepIndexChange: e => setStepIndex(e.target.value),
      allowStepLoop: true
      // content
      ,
      steps: items.map((item, index) => ({
        content: /*#__PURE__*/_jsxs(UIFlex, {
          direction: "column",
          align: "center",
          gap: 8,
          children: [/*#__PURE__*/_jsx(MediaCarouselItem, {
            item: item,
            fullscreen: fullscreen,
            onImageClick: () => {
              onItemClick === null || onItemClick === void 0 || onItemClick(item);
              onTrackInteraction === null || onTrackInteraction === void 0 || onTrackInteraction('click carousel screenshot');
              onFullscreenChange === null || onFullscreenChange === void 0 || onFullscreenChange(!fullscreen);
              setFullscreen(!fullscreen);
            },
            onTrackInteraction: onTrackInteraction
          }), /*#__PURE__*/_jsxs("span", {
            style: {
              color: fullscreen ? KOALA : SLINKY,
              fontSize: fullscreen ? undefined : MEDIUM_CLOSE_BUTTON_SIZE
            },
            children: [`${index + 1}/${items.length}`, item.alt ? ` - ${item.alt}` : '']
          })]
        })
      }))
    }, rest))
  });
}
function Fullscreen({
  fullscreen,
  onClose,
  children
}) {
  if (!fullscreen) return children;
  const contents = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(UICloseButton, {
      color: KOALA,
      onClick: onClose,
      style: {
        position: 'absolute',
        top: DISTANCE_MEASUREMENT_MEDIUM,
        right: DISTANCE_MEASUREMENT_MEDIUM
      }
    }), /*#__PURE__*/_jsx(UIDialogBody, {
      children: children
    })]
  });

  // if we're NOT in an iframe, we can use a modal
  if (window.self === window.top) return /*#__PURE__*/_jsx(UIModal, {
    use: "lightbox",
    children: contents
  });

  // if we ARE in an iframe, UIModal's transition looks really bad,
  // so we use UIFullScreen instead, with no transition
  return /*#__PURE__*/_jsx(UIFullScreen, {
    children: /*#__PURE__*/_jsx("div", {
      // focus ourselves, so pressing escape works
      // wait 60ms because of a race condition
      ref: el => setTimeout(() => el === null || el === void 0 ? void 0 : el.focus(), 60),
      style: {
        backgroundImage: `linear-gradient(${OBSIDIAN}, ${HEFFALUMP})`,
        height: '100vh',
        position: 'relative'
      },
      onKeyDown: e => {
        if (e.key === 'Escape') onClose();
      },
      tabIndex: 0,
      children: contents
    })
  });
}
function MediaCarouselItem({
  item,
  fullscreen,
  onImageClick,
  onTrackInteraction
}) {
  if (item.type === 'image') {
    return /*#__PURE__*/_jsx(UIClickable, {
      onClick: onImageClick,
      children: /*#__PURE__*/_jsx(UIImage, {
        src: item.source,
        alt: item.alt,
        shape: "rounded",
        style: {
          cursor: fullscreen ? 'zoom-out' : 'zoom-in',
          border: fullscreen ? undefined : `${BUTTON_BORDER_WIDTH} solid ${GREAT_WHITE}`,
          boxShadow: fullscreen ? `${CARD_SHADOW_COLOR} 0px ${DISTANCE_MEASUREMENT_EXTRA_SMALL} ${DISTANCE_MEASUREMENT_MEDIUM} 0px` : undefined,
          // This scales down the image to prevent a vertical scrollbar on
          // short screens. Horizontal scrollbar prevention is handled by
          // the carousel. 126px was found by trial and error.
          maxHeight: fullscreen ? 'calc(100vh - 126px)' : undefined
        }
      })
    });
  }
  return /*#__PURE__*/_jsx(VideoPlayer, {
    videoUrl: item.source,
    onTrackInteraction: onTrackInteraction
  });
}